import { useRef, useState } from 'react'
import { Heart, Pin, Trash2 } from 'lucide-react'

import { ThumbnailTab } from '@modules/component/ThumbnailTab/ThumbnailTab'
import { cn } from '@modules/lib/lib'
import { Button } from '@modules/shadcn/ui/Button/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@modules/shadcn/ui/Dialog/Dialog'

import type { ResolvedHistoryTitle } from '../utils/historyDisplay'

import { HistoryTitleEditor } from './HistoryTitleEditor'

interface AiImageCardProps {
  url?: string
  label?: string
  isSelected?: boolean
  onSelect?: () => void
  createdBy?: string
  createdAt?: string
  categoryTag?: string
  isLiked?: boolean
  onToggleLike?: () => void
  /**
   * 削除の実行。Promise を返す場合は完了まで待ち、その間 UI をロックする。
   *
   * 戻り値を `void` 固定にすると呼び出し側が渡す非同期関数の Promise を握り潰し、
   * 失敗が unhandled rejection になる（issue #2641）。必ず `Promise<void>` を許容する。
   */
  onDelete?: () => void | Promise<void>
  disableDelete?: boolean
  /** 履歴のタイトル（issue #2370）。サーバーの生の値（未設定は空文字列） */
  title?: string
  /** 表示用のタイトル（未設定時は既定表示）。指定があればサムネイルの下にタイトル行を出す */
  resolvedTitle?: ResolvedHistoryTitle
  /** タイトルの保存（企業はインライン編集）。未指定なら表示だけ */
  onSaveTitle?: (title: string) => Promise<void> | void
  /** ピン留め中か（issue #2953）。右上にピンを出す */
  isPinned?: boolean
  /** ピンの付け外し（企業のみ）。未指定なら操作ボタンを出さない */
  onTogglePin?: () => void
  /** 上限に達していてピン留めできない（ボタンは無効化せず、押すと入れ替えダイアログ） */
  pinLimitReached?: boolean
  /** ピン留めパネルのカードに出す「カット1」のようなラベル（左上） */
  cutLabel?: string
  /** 入力欄 id の接頭辞（複数カードで一意にする） */
  idPrefix?: string
}

/**
 * ai-image コンポーネント（サムネイルカード）
 *
 * サムネイル領域は共通 `ThumbnailTab` (modules) に集約。
 * 本コンポーネントは AI Color 固有の overlay (Heart / Delete / Pin / categoryTag / label / cutLabel) と
 * タイトル（issue #2370）・メタデータ表示 (createdBy / createdAt) のみを担当する。
 */
export const AiImageCard: React.FC<AiImageCardProps> = ({
  url,
  label,
  isSelected = false,
  onSelect,
  createdBy,
  createdAt,
  categoryTag,
  isLiked = false,
  onToggleLike,
  onDelete,
  disableDelete = false,
  title = '',
  resolvedTitle,
  onSaveTitle,
  isPinned = false,
  onTogglePin,
  pinLimitReached = false,
  cutLabel,
  idPrefix = 'ai-image-card',
}) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  // 連打ガードは ref で持つ。state の `isDeleting` は render 時点の値なので、
  // 同一タスク内の連続クリック（高速なダブルクリック・タッチの ghost click 等）は
  // 再レンダリングを挟まず 2 回とも false を読んでガードをすり抜ける。
  // STG 実機で同期連打すると削除 RPC が 3 回飛ぶことを実測したため ref に寄せた。
  const isDeletingRef = useRef(false)

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    if (isDeletingRef.current) return
    setIsDeleteDialogOpen(true)
  }

  // 削除完了（＝一覧の再取得）まではカードが画面に残るため、待たずにダイアログを閉じると
  // 同じ画像の削除をもう一度確定できてしまい、2 回目が not_found になる（issue #2641）。
  // 完了までダイアログを開いたままにして操作をロックする。
  const handleDeleteConfirm = async () => {
    if (isDeletingRef.current) return
    isDeletingRef.current = true
    setIsDeleting(true)
    try {
      await onDelete?.()
    } catch (error) {
      // エラーの UI 表示は onDelete の実装側（handleError によるトースト）の責務。
      // ここで握るのは「呼び出し側が catch し忘れても unhandled rejection にしない」ための保険。
      console.error('[AiImageCard] 画像の削除に失敗:', error)
    } finally {
      isDeletingRef.current = false
      setIsDeleting(false)
      setIsDeleteDialogOpen(false)
    }
  }

  const pinAriaLabel = isPinned
    ? 'ピン留めを外す'
    : pinLimitReached
      ? 'ピン留めする（上限に達しています）'
      : 'ピン留めする'
  const meta = [createdBy, createdAt].filter(Boolean).join('・')

  return (
    <div className="flex min-w-[9rem] flex-col gap-2">
      <ThumbnailTab
        ariaLabel={resolvedTitle?.text ?? label ?? createdBy ?? '画像を選択'}
        imageUrl={url}
        isSelected={isSelected}
        onSelect={onSelect}
      >
        {cutLabel ? (
          <span className="absolute left-2 top-2 rounded-md bg-slate-900/80 px-2 py-0.5 text-[10px] font-medium text-white">
            {cutLabel}
          </span>
        ) : null}
        {onTogglePin || isPinned ? (
          <button
            aria-label={pinAriaLabel}
            aria-pressed={isPinned}
            className={cn(
              'absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded shadow-sm',
              isPinned ? 'bg-orange-600 text-white' : 'bg-white/80 text-slate-600',
            )}
            disabled={!onTogglePin}
            onClick={event => {
              event.stopPropagation()
              onTogglePin?.()
            }}
            title={
              pinLimitReached && !isPinned
                ? 'ピン留めは4件までです。クリックすると入れ替える履歴を選べます'
                : undefined
            }
            type="button"
          >
            <Pin className={cn('h-4 w-4', isPinned && 'fill-current')} />
          </button>
        ) : null}
        {label ? (
          <span className="absolute bottom-2 right-2 rounded-md bg-slate-900/80 px-2 py-1 text-[11px] font-medium text-white">
            {label}
          </span>
        ) : null}
        <div className="absolute bottom-2 left-2 z-10 flex items-center gap-1">
          <button
            aria-label={isLiked ? 'お気に入りを解除' : 'お気に入りに追加'}
            aria-pressed={isLiked}
            className="flex h-6 w-6 items-center justify-center rounded bg-white/80 shadow-sm"
            disabled={!onToggleLike}
            onClick={event => {
              event.stopPropagation()
              onToggleLike?.()
            }}
            type="button"
          >
            <Heart className={cn('h-4 w-4', isLiked ? 'fill-emerald-500 text-emerald-500' : '')} />
          </button>
          {!disableDelete ? (
            <button
              aria-label="画像を削除"
              className="flex h-6 w-6 items-center justify-center rounded bg-white/80 shadow-sm"
              disabled={!onDelete || isDeleting}
              onClick={handleDeleteClick}
              type="button"
            >
              <Trash2 className="h-4 w-4 text-slate-600" />
            </button>
          ) : null}
          {categoryTag ? (
            <span className="rounded-full bg-[#111827] px-2 py-0.5 text-[10px] text-white">
              {categoryTag}
            </span>
          ) : null}
        </div>
      </ThumbnailTab>
      {resolvedTitle ? (
        <HistoryTitleEditor
          inputId={`${idPrefix}-title`}
          onSave={onSaveTitle}
          resolved={resolvedTitle}
          title={title}
        />
      ) : null}
      {/* 既定表示（日時 氏名）のときは同じ情報の重複になるので、作成者・日時の行は出さない（モックの状態1・3） */}
      {meta && !resolvedTitle?.isDefault ? (
        <p className="truncate text-[10px] text-slate-500">{meta}</p>
      ) : null}

      <Dialog
        open={isDeleteDialogOpen}
        onOpenChange={open => {
          // 削除中は閉じさせない（閉じられると同じカードから再度削除を確定できてしまう）
          if (isDeletingRef.current) return
          setIsDeleteDialogOpen(open)
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>画像の削除</DialogTitle>
            <DialogDescription>
              この生成画像を削除しますか？この操作は取り消せません。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              disabled={isDeleting}
              onClick={() => setIsDeleteDialogOpen(false)}
              type="button"
              variant="ghost"
            >
              キャンセル
            </Button>
            <Button
              disabled={isDeleting}
              onClick={handleDeleteConfirm}
              type="button"
              variant="destructive"
            >
              {isDeleting ? '削除中...' : '削除'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
